export const DRAW_LIFECYCLE_STATES = {
  WAITING: 'waiting',
  STARTED: 'started',
  MANUAL_ENVELOPE_PENDING: 'manual-envelope-pending',
  FINISHED: 'finished',
  REOPENED: 'reopened',
  EXPIRED: 'expired',
};

const normalizeStatusText = (value) => String(value || '').trim().toLowerCase();

export function normalizeDrawStatus(rawStatus) {
  const status = normalizeStatusText(
    typeof rawStatus === 'string'
      ? rawStatus
      : rawStatus?.status || rawStatus?.Status || rawStatus?.drawStatus || rawStatus?.DrawStatus
  );

  if (!status) return null;

  if (status.includes('manual') && status.includes('envelope') && status.includes('pending')) {
    return DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING;
  }

  if (status === 'waiting') return DRAW_LIFECYCLE_STATES.WAITING;
  if (status === 'started' || status === 'drawing' || status === 'inprogress' || status === 'in progress') {
    return DRAW_LIFECYCLE_STATES.STARTED;
  }
  if (status === 'finished' || status === 'complete' || status === 'completed' || status === 'drawn') {
    return DRAW_LIFECYCLE_STATES.FINISHED;
  }
  if (status === 'reopened' || status === 're-opened') return DRAW_LIFECYCLE_STATES.REOPENED;

  return null;
}

export function isCtaGame(gameType) {
  const value = normalizeStatusText(gameType).replace(/[-_\s]/g, '');
  return value === 'chasetheace';
}

export function isDrawLifecycleBlocking(state) {
  return [
    DRAW_LIFECYCLE_STATES.WAITING,
    DRAW_LIFECYCLE_STATES.STARTED,
    DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING,
    DRAW_LIFECYCLE_STATES.FINISHED,
    DRAW_LIFECYCLE_STATES.EXPIRED,
  ].includes(state);
}

export function getDrawLifecycleState({
  draw = null,
  countdownComplete = false,
  drawStatus = null,
  gameType = null,
  siteDetails = null,
  isManualEnvelopeMode = false,
  ctaDrawResult = null,
} = {}) {
  const normalizedStatus = normalizeDrawStatus(drawStatus);
  if (normalizedStatus && normalizedStatus !== DRAW_LIFECYCLE_STATES.REOPENED) {
    return normalizedStatus;
  }

  const resolvedGameType = gameType || draw?.game_type || draw?.gameType;
  const normalizedDrawStatus = normalizeDrawStatus(draw?.status || draw?.currentStatus || draw?.CurrentStatus);
  if (normalizedDrawStatus && normalizedDrawStatus !== DRAW_LIFECYCLE_STATES.REOPENED) {
    return normalizedDrawStatus;
  }

  const resultLocked = ctaDrawResult?.envelopeResultLocked === true
    || Boolean(ctaDrawResult?.cardDrawnRank && ctaDrawResult?.cardDrawnSuit);
  const drawStatusText = normalizeStatusText(draw?.status || draw?.currentStatus || draw?.CurrentStatus);
  const isManualPending = isCtaGame(resolvedGameType)
    && isManualEnvelopeMode
    && !resultLocked
    && (
      drawStatusText.includes('manual')
      || drawStatusText.includes('envelope')
      || siteDetails?.CTADrawInProgress === true
      || siteDetails?.ctaDrawInProgress === true
      || countdownComplete
    );

  if (isManualPending) return DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING;

  if (siteDetails?.CTADrawInProgress === true || siteDetails?.ctaDrawInProgress === true) {
    return DRAW_LIFECYCLE_STATES.STARTED;
  }

  if (countdownComplete) return DRAW_LIFECYCLE_STATES.EXPIRED;

  if (!draw) {
    return DRAW_LIFECYCLE_STATES.FINISHED;
  }

  return null;
}

export function getDrawLifecycleCopy(state) {
  switch (state) {
    case DRAW_LIFECYCLE_STATES.WAITING:
      return {
        titleKey: 'drawLifecycle.waitingTitle',
        bodyKey: 'drawLifecycle.waitingBody',
        titleFallback: 'Draw starting soon',
        bodyFallback: 'Purchases are closing while the draw is prepared.',
      };
    case DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING:
      return {
        titleKey: 'drawLifecycle.manualEnvelopeTitle',
        bodyKey: 'drawLifecycle.manualEnvelopeBody',
        titleFallback: 'An envelope is being selected',
        bodyFallback: 'The winning ticket has been drawn. The envelope/card result will be posted shortly.',
      };
    case DRAW_LIFECYCLE_STATES.FINISHED:
      return {
        titleKey: 'drawLifecycle.drawCompleteTitle',
        bodyKey: 'drawLifecycle.drawCompleteBody',
        titleFallback: 'Draw complete',
        bodyFallback: 'Results are being finalized. A new draw will open shortly.',
      };
    case DRAW_LIFECYCLE_STATES.REOPENED:
      return {
        titleKey: 'drawLifecycle.newDrawOpeningTitle',
        bodyKey: 'drawLifecycle.newDrawOpeningBody',
        titleFallback: 'New draw opening soon',
        bodyFallback: 'The next draw is loading now.',
      };
    case DRAW_LIFECYCLE_STATES.STARTED:
    case DRAW_LIFECYCLE_STATES.EXPIRED:
    default:
      return {
        titleKey: 'drawLifecycle.drawInProgressTitle',
        bodyKey: 'drawLifecycle.drawInProgressBody',
        titleFallback: 'Draw in progress',
        bodyFallback: 'Purchases are closed while the winning ticket is being selected.',
      };
  }
}

/**
 * Determines whether a draw is closed for ticket purchases.
 * Evaluates all signals across nextDraw and siteDetails.
 *
 * @param {Object} options
 * @param {Object|null} options.nextDraw - Mapped or raw next draw object
 * @param {Object|null} options.siteDetails - Site details / organization details
 * @returns {boolean} True if ticket purchasing is closed or unavailable
 */
export function isDrawClosed({ nextDraw = null, siteDetails = null } = {}) {
  // If neither nextDraw nor siteDetails has been loaded, cannot determine yet
  if (!nextDraw && !siteDetails) return false;

  // If siteDetails explicitly says purchasing is closed
  if (siteDetails?.closePurchasing === true || siteDetails?.ClosePurchasing === true) {
    return true;
  }

  // If CTA drawing is in progress
  if (siteDetails?.CTADrawInProgress === true || siteDetails?.ctaDrawInProgress === true) {
    return true;
  }

  // If site details indicate zero draws remaining and no active draw
  if (
    (siteDetails?.drawsRemaining === 0 || siteDetails?.DrawsRemaining === 0) &&
    (!nextDraw || nextDraw.cutoff_date_passed || nextDraw.cutOffDatePassed)
  ) {
    return true;
  }

  // If siteDetails is loaded but no next draw exists at all
  if (siteDetails && !nextDraw) {
    return true;
  }

  if (nextDraw) {
    // Cutoff date passed
    if (nextDraw.cutoff_date_passed === true || nextDraw.cutOffDatePassed === true) {
      return true;
    }

    // Status check
    const rawStatus = nextDraw.status || nextDraw.Status || nextDraw.currentStatus || nextDraw.CurrentStatus || '';
    const status = String(rawStatus).trim().toLowerCase();
    const closedStatuses = [
      'closed',
      'complete',
      'completed',
      'drawing',
      'finished',
      'drawn',
      'waiting',
      'started',
      'inprogress',
      'in progress',
      'expired',
      'manual-envelope-pending',
    ];
    if (closedStatuses.includes(status) || isDrawLifecycleBlocking(normalizeDrawStatus(rawStatus))) {
      return true;
    }

    // CTA cards remaining: 0 means Ace was found / game complete
    if (nextDraw.cards_remaining === 0 || nextDraw.cardsRemaining === 0) {
      return true;
    }

    // Draw date passed
    const rawDrawDate = nextDraw.draw_date || nextDraw.drawDate || nextDraw.nextDrawDate;
    if (rawDrawDate) {
      const drawTs = Date.parse(rawDrawDate);
      if (!Number.isNaN(drawTs) && Date.now() - drawTs > 60 * 1000) {
        return true;
      }
    }
  }

  return false;
}
